import React, { useMemo, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { uniq, isEmpty, capitalize } from 'lodash'
import pluralize from 'pluralize'

import WidgetContext from '@/components/widget/widget.context'
import { CHART } from '@/components/charts/chartPalette'
import WidgetContainer from '@/components/widget/WidgetContainer'
import WidgetTooltip from '@/components/widget/WidgetTooltip'
import BarChart from '@/components/charts/BarChart'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'

import { groupAndFormatCountByDate } from './widgetHelpers'

export const CountByDateWidget = ({ countedObj }) => {
  const { data, loading } = useSelector((state) => state.analytics)
  const { groupByValue, handleChangeCsvData } = useContext(WidgetContext)

  const formattedData = useMemo(
    () => groupAndFormatCountByDate(data[`${countedObj}CountsByDate`], groupByValue),
    [data, groupByValue]
  )

  const columns = useMemo(
    () => [
      {
        accessor: 'date',
        label: 'Date',
      },
      {
        accessor: `count`,
        label: `${capitalize(countedObj)} Count`,
        format: (v) => Number(v).toLocaleString(),
      },
    ],
    []
  )

  const tickValues = useMemo(
    () => uniq(formattedData.map((d) => d.date)).filter((item, i) => i % 2 === 0),
    [formattedData]
  )

  useEffect(() => {
    handleChangeCsvData({ headers: columns, data: formattedData })
  }, [formattedData])

  return (
    <WidgetContainer
      loading={loading.usagePage || loading.deckPage}
      noData={isEmpty(data[`${countedObj}CountsByDate`])}
      table={
        <AdvancedTable
          index="date"
          defaultOrderBy="date" // can't properly sort this due to not being a full date
          rows={formattedData}
          columns={columns}
          rowsPerPage={10}
          pagination
          enableUpdateRowsPerPage={false}
        />
      }
      chart={
        <BarChart
          data={formattedData}
          keys={[`count`]}
          indexBy="date"
          tooltip={(item) => (
            <WidgetTooltip color={item.color}>
              <h4>{item.data.date}</h4>
              <p>{`${pluralize(countedObj, Number(item.data.count).toLocaleString(), true)}`}</p>
            </WidgetTooltip>
          )}
          colors={[CHART.INFORMATIVE]}
          enableGridY
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Date',
            legendPosition: 'middle',
            legendOffset: 44,
            // What??
            // Remove every other item from the array so we don't have too many ticks ¯\_(ツ)_/¯
            ...(groupByValue === 'day' &&
              uniq(formattedData.map((d) => d.date)).length > 30 && {
                tickValues,
              }),
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: `${capitalize(countedObj)} Count`,
            legendPosition: 'middle',
            legendOffset: -60,
            format: (v) => Number(v).toLocaleString(),
          }}
          margin={{ top: 50, right: 10, bottom: 50, left: 70 }}
        />
      }
    />
  )
}
